import {
  ALLOCATION_ACCOUNT_TYPE_TO_DV_MAPPING,
  DOMAIN_VALUE_ALLOCATION_INVESTMENT_ACCOUNTS,
  DOMAIN_VALUE_ALLOCATION_GUARANTEED_FUNDS_ACCOUNTS,
  COMPUTED_ALLOCATION_INVESTMENT_ACCOUNT_CODE_TEMPLATE,
  COMPUTED_ALLOCATION_GUARANTEED_FUND_TEMPLATE,
  COMPUTED_ALLOCATION_ACCOUNTS,
  COMPUTED_ALLOCATION_ACCOUNT_ID,
  COMPUTED_ALLOCATION_ACCOUNT_TYPE,
  COMPUTED_ALLOCATION_ACCOUNT_FEES,
  COMPUTED_ALLOCATION_ACCOUNT_CODE,
  COMPUTED_ALLOCATION_ACCOUNT_AMOUNT,
  COMPUTED_ALLOCATION_ACCOUNT_AMOUNT_RAW,
  FORM_FIELD_ALLOCATION_FIRST_ACCOUNT,
  FORM_FIELD_ALLOCATION_INVESTMENT_ROUNDED,
  FORM_FIELD_ALLOCATION_EXISTING_INVESTMENT,
  FORM_FIELD_ALLOCATION_MAXIMUM_EXCEEDED,
  FORM_FIELD_ALLOCATION_MAX_BENEVA_INVESTMENT_ACCOUNTS,
  FORM_FIELD_ALLOCATION_MAX_BENEVA_GUARANTEED_FUNDS,
  DEFAULT_MAX_BENEVA_ACCOUNTS,
} from '@/constants/engine';
import {ActionFactory} from '@/engine/actions';
import {recursiveReplaceIndex} from '@/engine/utils/object';
import {AllocationAccount} from '@/repositories/beneva-api/endpoints/allocations';
import {BatchUpdate} from '@/types/document/store';
import {getBenevaInvestmentAccountCode, getBenevaGuaranteedInvestmentFundCode} from '@/utils/document/value';
import {getAllocationFormValues, getBenevaInvestmentAccounts, getBenevaGuaranteedInvestmentFunds} from '../utils';

// This function is a custom action that computes the predetermined allocations

export const computeAllocationAction: ActionFactory<'computeAllocation'> = (_, store, valueResolver) => {
  return () => {
    const updates: BatchUpdate[] = [];

    // Get the form values
    const formValues = getAllocationFormValues(store, valueResolver);
    if (!formValues) return false;
    const {selectedAllocation, fees, totalInvestment, accountsCount: previousAccountsCount, depositType} = formValues;
    const accountType = selectedAllocation.accountType ?? ALLOCATION_ACCOUNT_TYPE_TO_DV_MAPPING.CI;

    const isCI = accountType === ALLOCATION_ACCOUNT_TYPE_TO_DV_MAPPING.CI;

    const accounts = isCI
      ? getBenevaInvestmentAccounts(valueResolver)
      : getBenevaGuaranteedInvestmentFunds(valueResolver);

    const domainValueId = isCI
      ? DOMAIN_VALUE_ALLOCATION_INVESTMENT_ACCOUNTS
      : DOMAIN_VALUE_ALLOCATION_GUARANTEED_FUNDS_ACCOUNTS;

    const codeTemplate = isCI
      ? COMPUTED_ALLOCATION_INVESTMENT_ACCOUNT_CODE_TEMPLATE
      : COMPUTED_ALLOCATION_GUARANTEED_FUND_TEMPLATE;

    const maximumAccounts =
      Number(
        valueResolver({
          $ref: isCI
            ? FORM_FIELD_ALLOCATION_MAX_BENEVA_INVESTMENT_ACCOUNTS
            : FORM_FIELD_ALLOCATION_MAX_BENEVA_GUARANTEED_FUNDS,
        }),
      ) || DEFAULT_MAX_BENEVA_ACCOUNTS;

    // Compute the investment accounts
    let remainingInvestment = totalInvestment;
    let investmentRounded = false;
    let accountAlreadyExists = false;
    let newAccountsCount = new Set(accounts.map(account => account.accountCode)).size;

    const sortByAmount = (a: AllocationAccount, b: AllocationAccount) => a.amount - b.amount;
    selectedAllocation.accounts.sort(sortByAmount).forEach((account: AllocationAccount, index: number) => {
      // The last account, which is always the highest amount, gets the remaining investment
      let amount = remainingInvestment;
      if (index < selectedAllocation.accounts.length - 1) {
        amount = Math.round((totalInvestment / 100) * account.amount);
        remainingInvestment -= amount;
      } else if (remainingInvestment <= 0) {
        amount = 0;
      }

      if (amount != (totalInvestment / 100) * account.amount) {
        investmentRounded = true;
      }

      // Set the row fields
      updates.push({
        type: 'set',
        id: `_${COMPUTED_ALLOCATION_ACCOUNTS}#${index}[fields]`,
        value: recursiveReplaceIndex(
          [
            COMPUTED_ALLOCATION_ACCOUNT_ID,
            COMPUTED_ALLOCATION_ACCOUNT_TYPE,
            COMPUTED_ALLOCATION_ACCOUNT_FEES,
            COMPUTED_ALLOCATION_ACCOUNT_CODE,
            COMPUTED_ALLOCATION_ACCOUNT_AMOUNT,
            COMPUTED_ALLOCATION_ACCOUNT_AMOUNT_RAW,
          ].join(','),
          index,
        ),
      });

      // Set the account's id
      const accountIdField = recursiveReplaceIndex(COMPUTED_ALLOCATION_ACCOUNT_ID, index);
      const accountId = `${domainValueId}#${account.accountId}`;
      updates.push({type: 'set', id: accountIdField, value: {$ref: accountId}});

      // Set the account's type
      const accountTypeField = recursiveReplaceIndex(COMPUTED_ALLOCATION_ACCOUNT_TYPE, index);
      updates.push({type: 'set', id: accountTypeField, value: {$ref: accountType}});

      // Set the fees
      const accountFeesField = recursiveReplaceIndex(COMPUTED_ALLOCATION_ACCOUNT_FEES, index);
      updates.push({type: 'set', id: accountFeesField, value: fees});

      // Set the Beneva code
      const accountCodeTemplate = recursiveReplaceIndex({$template: codeTemplate}, index);
      const accountCodeField = recursiveReplaceIndex(COMPUTED_ALLOCATION_ACCOUNT_CODE, index);
      updates.push({type: 'set', id: accountCodeField, value: accountCodeTemplate});

      // Set the account's computed amount
      const amountField = recursiveReplaceIndex(COMPUTED_ALLOCATION_ACCOUNT_AMOUNT, index);
      updates.push({type: 'set', id: amountField, value: `${amount} %`});
      const rawAmountField = recursiveReplaceIndex(COMPUTED_ALLOCATION_ACCOUNT_AMOUNT_RAW, index);
      updates.push({type: 'set', id: rawAmountField, value: `${amount}`});

      // Check if the account already exists
      const accountCode = isCI
        ? getBenevaInvestmentAccountCode({$ref: accountId}, fees, valueResolver)
        : getBenevaGuaranteedInvestmentFundCode({$ref: accountId}, fees, valueResolver);

      if (accounts.some(account => account.accountCode === accountCode && account.depositType == depositType)) {
        accountAlreadyExists = true;
      } else if (!accounts.some(account => account.accountCode === accountCode)) {
        newAccountsCount++;
      }

      if (index == 0) {
        // Set the first account code in a specific field for an extra validation
        updates.push({type: 'set', id: FORM_FIELD_ALLOCATION_FIRST_ACCOUNT, value: accountCode});
      }
    });
    updates.push({
      type: 'set',
      id: `_${COMPUTED_ALLOCATION_ACCOUNTS}[count]`,
      value: `${selectedAllocation.accounts.length}`,
    });

    // Clean up old accounts
    for (let i = selectedAllocation.accounts.length; i < previousAccountsCount; i++) {
      updates.push({type: 'unset', id: recursiveReplaceIndex(COMPUTED_ALLOCATION_ACCOUNT_ID, i)});
      updates.push({type: 'unset', id: recursiveReplaceIndex(COMPUTED_ALLOCATION_ACCOUNT_TYPE, i)});
      updates.push({type: 'unset', id: recursiveReplaceIndex(COMPUTED_ALLOCATION_ACCOUNT_FEES, i)});
      updates.push({type: 'unset', id: recursiveReplaceIndex(COMPUTED_ALLOCATION_ACCOUNT_CODE, i)});
      updates.push({type: 'unset', id: recursiveReplaceIndex(COMPUTED_ALLOCATION_ACCOUNT_AMOUNT, i)});
      updates.push({type: 'unset', id: recursiveReplaceIndex(COMPUTED_ALLOCATION_ACCOUNT_AMOUNT_RAW, i)});
    }

    // Sets the warning flag if the investment was rounded
    updates.push({
      type: 'set',
      id: FORM_FIELD_ALLOCATION_INVESTMENT_ROUNDED,
      value: investmentRounded ? 'true' : 'false',
    });

    // Sets the warning flag if an account already exists
    updates.push({
      type: 'set',
      id: FORM_FIELD_ALLOCATION_EXISTING_INVESTMENT,
      value: accountAlreadyExists ? 'true' : 'false',
    });

    // Sets the error if the maximum number of accounts is exceeded
    updates.push({
      type: 'set',
      id: FORM_FIELD_ALLOCATION_MAXIMUM_EXCEEDED,
      value: newAccountsCount > maximumAccounts ? 'true' : 'false',
    });

    store.getState().batchUpdate(updates);

    return true;
  };
};
